import { Component } from '@theme/component';

/**
 * Every connected countdown shares one 1-second ticker, so a collection page with many
 * coming-soon cards runs a single interval.
 *
 * @type {Set<ComingSoonCountdown>}
 */
const countdowns = new Set();
let tickerId = 0;

function tick() {
  const now = Date.now();
  for (const countdown of countdowns) countdown.update(now);
}

/** @param {ComingSoonCountdown} countdown */
function subscribe(countdown) {
  countdowns.add(countdown);
  if (!tickerId) tickerId = window.setInterval(tick, 1000);
}

/** @param {ComingSoonCountdown} countdown */
function unsubscribe(countdown) {
  countdowns.delete(countdown);
  if (countdowns.size === 0 && tickerId) {
    window.clearInterval(tickerId);
    tickerId = 0;
  }
}

/** Spread launch-time reloads over a few seconds so every open PDP doesn't hit Shopify at once. */
const RELOAD_JITTER_MS = 3000;

/**
 * Coming-soon countdown. `data-launch` is the launch time in Unix seconds; `data-complete` is
 * what happens at zero: `reload` (PDP, so Liquid renders the product form) or `hide` (cards).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends Component<Refs>
 */
class ComingSoonCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  #launch = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#launch = Number(this.dataset.launch) * 1000;
    if (!this.#launch) return;

    subscribe(this);
    this.update(Date.now());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unsubscribe(this);
  }

  /** @param {number} now */
  update(now) {
    const remaining = Math.floor((this.#launch - now) / 1000);

    if (remaining <= 0) {
      unsubscribe(this);
      this.#complete();
      return;
    }

    this.#render('days', Math.floor(remaining / 86400));
    this.#render('hours', Math.floor((remaining % 86400) / 3600));
    this.#render('minutes', Math.floor((remaining % 3600) / 60));
    this.#render('seconds', remaining % 60);
  }

  /**
   * @param {keyof Refs} unit
   * @param {number} value
   */
  #render(unit, value) {
    const text = String(value).padStart(2, '0');
    const element = this.refs[unit];
    if (element.textContent !== text) element.textContent = text;
  }

  #complete() {
    ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => this.#render(/** @type {keyof Refs} */ (unit), 0));

    if (this.dataset.complete !== 'reload') {
      (this.closest('[data-coming-soon-root]') ?? this).setAttribute('hidden', '');
      return;
    }

    // Reload once per launch. If the reloaded page is still the pre-launch render (page cache,
    // or a browser clock ahead of Shopify's), hide the timer instead of reloading in a loop.
    const guardKey = `coming-soon-reloaded:${this.dataset.launch}`;
    try {
      if (window.sessionStorage.getItem(guardKey)) {
        this.setAttribute('hidden', '');
        return;
      }
      window.sessionStorage.setItem(guardKey, '1');
    } catch {
      // Storage unavailable: reload without the guard.
    }

    window.setTimeout(() => window.location.reload(), Math.random() * RELOAD_JITTER_MS);
  }
}

if (!customElements.get('coming-soon-countdown-component')) {
  customElements.define('coming-soon-countdown-component', ComingSoonCountdown);
}
